import React, { useEffect, useRef, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  cleanupWidgetTestArtifacts,
  testWidgetScript,
} from "./widgetScriptTest";

const TESTABLE_SCRIPT_KEYS = new Set(["staging", "prod"]);
const LEGACY_SCRIPT_KEY = "legacy";
const SAVED_CONFIG_HINT =
  "Save changes before testing. Test uses the saved business configuration.";
const SCRIPT_UNAVAILABLE_HINT = "Script is unavailable.";
const SCRIPT_CHANGED_MESSAGE =
  "Widget test reset because the saved widget script changed.";

const formatEnvironment = (value) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const getLoadedMessage = (environmentLabel, resultStatus) => {
  if (resultStatus === "already-loaded") {
    return `${environmentLabel} widget already loaded`;
  }

  if (resultStatus === "loading") {
    return `${environmentLabel} widget is still loading`;
  }

  return `${environmentLabel} widget loaded`;
};

const getResultSeverity = (resultStatus) =>
  resultStatus === "loading" ? "info" : "success";

const getTestableScriptKey = (scriptSections) =>
  scriptSections
    .filter((section) => TESTABLE_SCRIPT_KEYS.has(section.key))
    .map((section) => `${section.key}:${section.value || ""}`)
    .join("\n");

function WidgetScriptSection({
  scriptSections,
  copiedTarget,
  onCopy,
  isDirty,
  cardClassName,
}) {
  const [testStatus, setTestStatus] = useState(null);
  const [showLegacyScript, setShowLegacyScript] = useState(false);
  const testableScriptKey = getTestableScriptKey(scriptSections);
  const hasStartedTestWidgetRef = useRef(false);
  const isMountedRef = useRef(false);
  const latestTestRequestRef = useRef(0);
  const activeTestEnvironmentRef = useRef(null);
  const activeTestScriptHtmlRef = useRef(null);
  const latestTestableScriptKeyRef = useRef(testableScriptKey);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (hasStartedTestWidgetRef.current) {
        cleanupWidgetTestArtifacts();
      }
    };
  }, []);

  useEffect(() => {
    if (!isDirty || !hasStartedTestWidgetRef.current) {
      return;
    }

    latestTestRequestRef.current += 1;
    activeTestEnvironmentRef.current = null;
    activeTestScriptHtmlRef.current = null;
    cleanupWidgetTestArtifacts();
    hasStartedTestWidgetRef.current = false;
    setTestStatus({
      severity: "info",
      message: SAVED_CONFIG_HINT,
    });
  }, [isDirty]);

  useEffect(() => {
    if (latestTestableScriptKeyRef.current === testableScriptKey) {
      return;
    }

    latestTestableScriptKeyRef.current = testableScriptKey;

    if (!hasStartedTestWidgetRef.current) {
      return;
    }

    latestTestRequestRef.current += 1;
    activeTestEnvironmentRef.current = null;
    activeTestScriptHtmlRef.current = null;
    cleanupWidgetTestArtifacts();
    hasStartedTestWidgetRef.current = false;
    setTestStatus({
      severity: "info",
      message: SCRIPT_CHANGED_MESSAGE,
    });
  }, [testableScriptKey]);

  const getDisabledReason = (section) => {
    if (isDirty) {
      return SAVED_CONFIG_HINT;
    }

    if (!section.value) {
      return SCRIPT_UNAVAILABLE_HINT;
    }

    return "";
  };

  const handleDisabledTestClick = (reason) => {
    if (!reason) {
      return;
    }

    setTestStatus({
      severity: "info",
      message: reason,
    });
  };

  const handleToggleLegacyScript = () => {
    setShowLegacyScript((current) => !current);
  };

  const isCurrentTestRequest = (requestId) =>
    isMountedRef.current && latestTestRequestRef.current === requestId;

  const handleTestClick = async (section) => {
    const environmentLabel = formatEnvironment(section.key);
    const isSameEnvironmentAttempt =
      activeTestEnvironmentRef.current === section.key &&
      activeTestScriptHtmlRef.current === section.value;
    const requestId = isSameEnvironmentAttempt
      ? latestTestRequestRef.current
      : latestTestRequestRef.current + 1;

    latestTestRequestRef.current = requestId;
    activeTestEnvironmentRef.current = section.key;
    activeTestScriptHtmlRef.current = section.value;
    hasStartedTestWidgetRef.current = true;

    setTestStatus({
      severity: "info",
      message: `Loading ${section.key} widget...`,
    });

    try {
      const result = await testWidgetScript({
        environment: section.key,
        scriptHtml: section.value,
      });

      if (!isCurrentTestRequest(requestId)) {
        return;
      }

      setTestStatus({
        severity: getResultSeverity(result.status),
        message: getLoadedMessage(environmentLabel, result.status),
      });
    } catch (error) {
      if (!isCurrentTestRequest(requestId)) {
        return;
      }

      activeTestEnvironmentRef.current = null;
      activeTestScriptHtmlRef.current = null;
      hasStartedTestWidgetRef.current = false;
      setTestStatus({
        severity: "error",
        message: `Failed to load ${section.key} widget`,
      });
    }
  };

  return (
    <Card className={cardClassName}>
      <CardContent>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Web widget code
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Copy the code snippet below and paste it on your website page
        </Typography>
        <Stack spacing={3}>
          {scriptSections.map((section) => {
            if (section.key === LEGACY_SCRIPT_KEY && !showLegacyScript) {
              return (
                <Box key={section.key}>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={handleToggleLegacyScript}
                  >
                    Show legacy script
                  </Link>
                </Box>
              );
            }

            const isTestable = TESTABLE_SCRIPT_KEYS.has(section.key);
            const testDisabledReason = isTestable
              ? getDisabledReason(section)
              : "";

            return (
              <React.Fragment key={section.key}>
                {section.key === LEGACY_SCRIPT_KEY && (
                  <Box>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={handleToggleLegacyScript}
                    >
                      Hide legacy script
                    </Link>
                  </Box>
                )}
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {section.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      name={`script-${section.key}`}
                      type="text"
                      minRows={4}
                      multiline
                      variant="outlined"
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          py: 1.5,
                        },
                      }}
                      disabled
                      value={section.value}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => onCopy(section.value, section.key)}
                        startIcon={<ContentCopyIcon />}
                        disabled={!section.value}
                      >
                        {copiedTarget === section.key ? "Copied!" : "Copy Text"}
                      </Button>
                      {isTestable && (
                        <Box
                          component="span"
                          data-testid={`widget-test-${section.key}-wrapper`}
                          title={testDisabledReason}
                          sx={{ display: "inline-flex" }}
                          onClick={() => handleDisabledTestClick(testDisabledReason)}
                        >
                          <Button
                            variant="contained"
                            onClick={() => handleTestClick(section)}
                            startIcon={<PlayArrowIcon />}
                            disabled={Boolean(testDisabledReason)}
                          >
                            Test
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </React.Fragment>
            );
          })}
          {testStatus && (
            <Alert severity={testStatus.severity} role="status">
              {testStatus.message}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default WidgetScriptSection;
